import { Box } from '@opendoor/atoms/box';
import { cn } from '@opendoor/atoms/utils';
import type { EntryComponent } from 'cms/entries/entries';
import type { ILpComponentSpacer } from 'declarations/contentful';

import { getComponentThemeColors } from './shared/styles/ComponentTheme';

const bgColorToClass: Record<string, string> = {
  white0: 'bg-od-bg-primary',
  warmgrey100: 'bg-od-bg-secondary',
  earth100: 'bg-od-bg-secondary',
  earth200: 'bg-od-bg-tertiary',
  brandBlue600: 'bg-od-bg-accent-brand',
  warmgrey200: 'bg-od-bg-tertiary',
  warmgrey800: 'bg-od-bg-inverse-primary',
  warmgrey950: 'bg-od-bg-inverse-primary',
  neutrals100: 'bg-od-bg-inverse-primary',
};

// CMS spacer heights are a fixed px enum; map each to its Tailwind scale class
// (scale step = px / 4). Literal class strings are required so Tailwind's JIT
// emits them. Mobile heights apply at the base breakpoint; desktop heights at
// `md` (768px), mirroring the original Bricks `$largerThanSM` override.
const mobileHeightClass: Record<number, string> = {
  0: 'h-0',
  8: 'h-2',
  16: 'h-4',
  24: 'h-6',
  32: 'h-8',
  40: 'h-10',
  48: 'h-12',
  56: 'h-14',
  64: 'h-16',
  72: 'h-18',
  80: 'h-20',
};

const desktopHeightClass: Record<number, string> = {
  0: 'md:h-0',
  8: 'md:h-2',
  16: 'md:h-4',
  24: 'md:h-6',
  32: 'md:h-8',
  40: 'md:h-10',
  48: 'md:h-12',
  56: 'md:h-14',
  64: 'md:h-16',
  72: 'md:h-18',
  80: 'md:h-20',
  96: 'md:h-24',
  104: 'md:h-26',
  112: 'md:h-28',
  120: 'md:h-30',
  128: 'md:h-32',
  144: 'md:h-36',
  160: 'md:h-40',
  176: 'md:h-44',
  192: 'md:h-48',
  208: 'md:h-52',
  224: 'md:h-56',
  240: 'md:h-60',
};

const RenderSpacer = (entry: ILpComponentSpacer) => {
  const { fields } = entry;
  const componentTheme = fields.backgroundColor
    ? getComponentThemeColors(fields.backgroundColor)
    : getComponentThemeColors('White 0');

  const bgColor = componentTheme.bg || 'white0';
  const bgClass = bgColorToClass[bgColor] || 'bg-od-bg-primary';
  const mobileHeight = parseInt(fields.mobileSpace, 10);
  // `!= null` so an authored desktop "0" stays 0 rather than inheriting mobile.
  const desktopHeight = fields.space != null ? parseInt(fields.space, 10) : mobileHeight;

  return (
    <Box
      aria-hidden
      className={cn(
        'lp-spacer block',
        bgClass,
        mobileHeightClass[mobileHeight],
        desktopHeightClass[desktopHeight],
      )}
    />
  );
};

const Spacer: EntryComponent<ILpComponentSpacer> = {
  render: RenderSpacer,
};

export default Spacer;
